/**
 * ZoomableImage — 이미지 영역만 사용자 줌 가능하게 감싸는 래퍼.
 *
 * 배경: index.html viewport 가 `user-scalable=no` 라 브라우저 기본 핀치줌이 전역으로
 * 막혀 있다(페이지 전체 줌 방지 의도). 그래서 "딱 이미지 부분만" 줌하려면 이 컴포넌트가
 * 해당 영역의 제스처를 직접 처리한다.
 *
 * 제스처:
 *   - 두 손가락 핀치 → 줌 인/아웃 (중심점 고정)
 *   - 더블탭 → 줌 토글 (탭 지점 기준 2.5배 ↔ 1배)
 *   - 줌 상태(scale>1)에서 한 손가락 드래그 → 패닝
 *
 * ⚠ design 원본에는 캐러셀 공존용 `onEdgeDrag`/`onEdgeDragEnd`(가장자리 초과량을
 *   부모 트랙에 보고)와 `ProductImage`/`thumbWidth` 경로가 더 있다. dev 에는 그 캐러셀도
 *   ProductImage 도 없어서 옮기지 않았다 — 쓰는 곳이 생기면 design 에서 그대로 가져온다.
 *   scale==1 단일 포인터를 부모로 통과시키는 규칙은 그대로 살아 있어, 나중에 스와이프
 *   부모가 생겨도 이 컴포넌트를 고칠 일은 없다.
 */
import { useRef, useState, type ReactNode } from "react";
import styles from "./ZoomableImage.module.css";

interface ZoomableImageProps {
  src?: string | null;
  /**
   * src 대신 임의 노드를 확대 대상으로 쓴다 (SC-008 카드 원본 뷰어).
   * 이미지가 없는 카드에는 플레이스홀더가 들어와야 해서 열어둔 슬롯이다.
   * children 이 있으면 <img> 는 그리지 않는다.
   */
  children?: ReactNode;
  alt: string;
  /** 확대보기는 전체 이미지 확인이 목적이라 기본 'contain' — 잘라내면 볼 수 없다. */
  fit?: "cover" | "contain";
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
/** 두 탭이 더블탭으로 인정되는 최대 간격(ms). */
const DOUBLE_TAP_MS = 300;
/** 탭으로 인정되는 최대 이동 거리(px) — 이보다 움직이면 드래그로 본다. */
const TAP_MOVE_TOL = 10;

interface Point {
  x: number;
  y: number;
}

interface Transform {
  scale: number;
  x: number;
  y: number;
}

type Mode = "idle" | "pan" | "pinch";

interface GestureState {
  pointers: Map<number, Point>;
  mode: Mode;
  startDist: number;
  startMid: Point;
  startScale: number;
  startTranslate: Point;
  panStart: Point;
  rect: { w: number; h: number; cx: number; cy: number };
  downPos: Point;
  moved: boolean;
  lastTapTime: number;
  lastTapPos: Point;
}

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

const distance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

export default function ZoomableImage({
  src,
  children,
  alt,
  fit = "contain",
}: ZoomableImageProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  const tRef = useRef<Transform>(transform);

  const g = useRef<GestureState>({
    pointers: new Map(),
    mode: "idle",
    startDist: 0,
    startMid: { x: 0, y: 0 },
    startScale: 1,
    startTranslate: { x: 0, y: 0 },
    panStart: { x: 0, y: 0 },
    rect: { w: 0, h: 0, cx: 0, cy: 0 },
    downPos: { x: 0, y: 0 },
    moved: false,
    lastTapTime: 0,
    lastTapPos: { x: 0, y: 0 },
  });

  const readRect = () => {
    const el = viewportRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    g.current.rect = { w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  };

  /** scale 에 맞춰 translate 를 컨테이너 밖으로 벗어나지 않게 제한. */
  const clampTranslate = (x: number, y: number, scale: number): Point => {
    const { w, h } = g.current.rect;
    const maxX = Math.max(0, ((scale - 1) * w) / 2);
    const maxY = Math.max(0, ((scale - 1) * h) / 2);
    return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  };

  const apply = (scale: number, x: number, y: number) => {
    const s = clamp(scale, MIN_SCALE, MAX_SCALE);
    const t = clampTranslate(x, y, s);
    const next = { scale: s, x: t.x, y: t.y };
    tRef.current = next;
    setTransform(next);
  };

  const animateTo = (scale: number, x: number, y: number) => {
    g.current.mode = "idle";
    apply(scale, x, y);
  };

  const toggleDoubleTap = (tapX: number, tapY: number) => {
    readRect();
    if (tRef.current.scale > 1.01) {
      animateTo(1, 0, 0);
      return;
    }
    const { cx, cy } = g.current.rect;
    const mx = tapX - cx;
    const my = tapY - cy;
    // startScale=1, startTranslate=0 → translate = M*(1-scale)
    animateTo(DOUBLE_TAP_SCALE, mx * (1 - DOUBLE_TAP_SCALE), my * (1 - DOUBLE_TAP_SCALE));
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const gs = g.current;
    gs.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (gs.pointers.size === 1) {
      gs.downPos = { x: e.clientX, y: e.clientY };
      gs.moved = false;
    }

    if (gs.pointers.size === 2) {
      // 핀치 시작.
      readRect();
      const [a, b] = [...gs.pointers.values()];
      gs.startDist = distance(a, b);
      gs.startMid = { x: (a.x + b.x) / 2 - gs.rect.cx, y: (a.y + b.y) / 2 - gs.rect.cy };
      gs.startScale = tRef.current.scale;
      gs.startTranslate = { x: tRef.current.x, y: tRef.current.y };
      gs.mode = "pinch";
      e.currentTarget.setPointerCapture(e.pointerId);
      e.stopPropagation();
    } else if (gs.pointers.size === 1 && tRef.current.scale > 1) {
      // 줌 상태 단일 포인터 → 패닝. 부모 스와이프 차단.
      readRect();
      gs.panStart = { x: e.clientX, y: e.clientY };
      gs.startTranslate = { x: tRef.current.x, y: tRef.current.y };
      gs.mode = "pan";
      e.currentTarget.setPointerCapture(e.pointerId);
      e.stopPropagation();
    }
    // scale==1 단일 포인터: 아무것도 하지 않고 부모로 통과.
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const gs = g.current;
    if (!gs.pointers.has(e.pointerId)) return;
    gs.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (distance({ x: e.clientX, y: e.clientY }, gs.downPos) > TAP_MOVE_TOL) {
      gs.moved = true;
    }

    if (gs.mode === "pinch" && gs.pointers.size >= 2) {
      const [a, b] = [...gs.pointers.values()];
      const d = distance(a, b);
      if (gs.startDist === 0) return;
      const scale1 = clamp((gs.startScale * d) / gs.startDist, MIN_SCALE, MAX_SCALE);
      const curMid = { x: (a.x + b.x) / 2 - gs.rect.cx, y: (a.y + b.y) / 2 - gs.rect.cy };
      const ratio = scale1 / gs.startScale;
      const x1 = curMid.x - (gs.startMid.x - gs.startTranslate.x) * ratio;
      const y1 = curMid.y - (gs.startMid.y - gs.startTranslate.y) * ratio;
      apply(scale1, x1, y1);
      e.stopPropagation();
    } else if (gs.mode === "pan") {
      e.stopPropagation();
      const dx = e.clientX - gs.panStart.x;
      const dy = e.clientY - gs.panStart.y;
      apply(tRef.current.scale, gs.startTranslate.x + dx, gs.startTranslate.y + dy);
    }
  };

  /** 단일 포인터가 움직임 없이 끝났을 때 탭/더블탭 판정. 더블탭이면 줌 토글. */
  const handleTap = (e: React.PointerEvent<HTMLDivElement>) => {
    const gs = g.current;
    const now = performance.now();
    const pos = { x: e.clientX, y: e.clientY };
    if (now - gs.lastTapTime < DOUBLE_TAP_MS && distance(pos, gs.lastTapPos) < TAP_MOVE_TOL * 2) {
      toggleDoubleTap(pos.x, pos.y);
      gs.lastTapTime = 0;
      e.stopPropagation();
    } else {
      gs.lastTapTime = now;
      gs.lastTapPos = pos;
    }
  };

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const gs = g.current;
    const wasMode = gs.mode;
    gs.pointers.delete(e.pointerId);

    if (wasMode === "pinch") {
      e.stopPropagation();
      if (gs.pointers.size < 2) {
        if (tRef.current.scale <= 1.01) {
          animateTo(1, 0, 0);
        } else if (gs.pointers.size === 1) {
          // 한 손가락 남음 → 패닝으로 전환.
          const [p] = [...gs.pointers.values()];
          gs.panStart = { x: p.x, y: p.y };
          gs.startTranslate = { x: tRef.current.x, y: tRef.current.y };
          gs.mode = "pan";
        } else {
          gs.mode = "idle";
          setTransform({ ...tRef.current }); // transition 재적용 위해 리렌더.
        }
      }
      return;
    }

    if (wasMode === "pan") {
      e.stopPropagation();
      if (gs.pointers.size === 0) {
        gs.mode = "idle";
        if (gs.moved) {
          setTransform({ ...tRef.current });
        } else {
          // 움직임 없었으면 더블탭(줌아웃) 판정.
          handleTap(e);
        }
      }
      return;
    }

    // idle 단일 포인터 종료 → 탭/더블탭 판정 (scale==1).
    if (gs.pointers.size === 0 && !gs.moved) {
      handleTap(e);
    }
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const gs = g.current;
    gs.pointers.delete(e.pointerId);
    if (gs.pointers.size === 0) {
      gs.mode = "idle";
      if (tRef.current.scale <= 1.01) animateTo(1, 0, 0);
    }
  };

  const isGesturing = g.current.mode === "pan" || g.current.mode === "pinch";

  return (
    <div
      ref={viewportRef}
      className={styles.viewport}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={onPointerCancel}
    >
      <div
        className={styles.content}
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transition: isGesturing ? "none" : "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {children ?? (
          <img className={styles.image} src={src ?? undefined} alt={alt} style={{ objectFit: fit }} />
        )}
      </div>
    </div>
  );
}
